/**
 * ForeignKeyActionRadios — presentational component for ON DELETE / ON UPDATE
 * radios + per-engine extras (MATCH, DEFERRABLE, WITH NOCHECK). Split out of
 * `ForeignKeyAddFormBody` to keep that file under the 200-line budget; see the
 * Slice A4 collation pattern (`ColumnAddBasicFields` + `ColumnGeneratedFields`)
 * for the orchestrator-owns-state / presentational-children-accept-setters
 * convention.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice B2.
 */
import type { Dispatch, SetStateAction } from 'react';

import { Checkbox } from '../../../../../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../../../../../components/ui/radio-group';
import type { SqlEngine } from '../../../../sql-grammar/model';
import type { ReferentialAction } from '../../../../sql-resolver/types/ddlEdit';

type ActionMode = 'unspecified' | ReferentialAction;
type DeferrableMode = 'unspecified' | 'deferred' | 'immediate';
type MatchMode = 'unspecified' | 'full' | 'partial' | 'simple';

const ALL_ACTIONS: readonly ReferentialAction[] = [
  'no-action', 'restrict', 'cascade', 'set-null', 'set-default',
];
const ACTION_LABELS: Record<ReferentialAction, string> = {
  'no-action': 'NO ACTION',
  'restrict': 'RESTRICT',
  'cascade': 'CASCADE',
  'set-null': 'SET NULL',
  'set-default': 'SET DEFAULT',
};
const MATCH_MODES: readonly MatchMode[] = ['unspecified', 'full', 'partial', 'simple'];
const DEFERRABLE_MODES: readonly DeferrableMode[] = ['unspecified', 'deferred', 'immediate'];

export interface ForeignKeyActionRadiosProps {
  readonly testId: string;
  readonly engine: SqlEngine;
  readonly showOnUpdate: boolean;
  readonly showMatch: boolean;
  readonly showDeferrable: boolean;
  readonly showWithNoCheck: boolean;
  readonly onDelete: ActionMode;
  readonly onUpdate: ActionMode;
  readonly match: MatchMode;
  readonly deferrable: DeferrableMode;
  readonly withNoCheck: boolean;
  readonly setOnDelete: Dispatch<SetStateAction<ActionMode>>;
  readonly setOnUpdate: Dispatch<SetStateAction<ActionMode>>;
  readonly setMatch: Dispatch<SetStateAction<MatchMode>>;
  readonly setDeferrable: Dispatch<SetStateAction<DeferrableMode>>;
  readonly setWithNoCheck: Dispatch<SetStateAction<boolean>>;
  readonly isActionAllowed: (action: ReferentialAction) => boolean;
}

export function ForeignKeyActionRadios(props: ForeignKeyActionRadiosProps) {
  const {
    testId, engine, showOnUpdate, showMatch, showDeferrable, showWithNoCheck,
    onDelete, onUpdate, match, deferrable, withNoCheck,
    setOnDelete, setOnUpdate, setMatch, setDeferrable, setWithNoCheck, isActionAllowed,
  } = props;
  return (
    <>
      <Fieldset legend="ON DELETE">
        <ActionPicker testId={`${testId}-on-delete`} mode={onDelete} setMode={setOnDelete} isAllowed={isActionAllowed} />
      </Fieldset>
      {showOnUpdate ? (
        <Fieldset legend="ON UPDATE">
          <ActionPicker testId={`${testId}-on-update`} mode={onUpdate} setMode={setOnUpdate} isAllowed={isActionAllowed} />
        </Fieldset>
      ) : (
        <div
          className="rounded-md border border-border bg-muted/40 px-2 py-1 text-[10px] text-muted-foreground"
          data-testid={`${testId}-on-update-unsupported-note`}
        >
          {engine === 'oracle' ? 'Oracle has no ON UPDATE referential action.' : 'ON UPDATE unsupported on this engine.'}
        </div>
      )}
      {showMatch ? (
        <Fieldset legend="MATCH">
          <RadioRow testId={`${testId}-match`} mode={match} setMode={setMatch} options={MATCH_MODES} />
        </Fieldset>
      ) : null}
      {showDeferrable ? (
        <Fieldset legend="DEFERRABLE">
          <RadioRow testId={`${testId}-deferrable`} mode={deferrable} setMode={setDeferrable} options={DEFERRABLE_MODES} />
        </Fieldset>
      ) : null}
      {showWithNoCheck ? (
        <label className="flex items-center gap-2 text-xs" data-testid={`${testId}-with-nocheck-label`}>
          <Checkbox
            checked={withNoCheck}
            onCheckedChange={(checked) => setWithNoCheck(checked === true)}
            data-testid={`${testId}-with-nocheck`}
          />
          <span>WITH NOCHECK — suppress legacy-data validation</span>
        </label>
      ) : null}
    </>
  );
}

function Fieldset({ legend, children }: { legend: string; children: React.ReactNode }) {
  return (
    <fieldset className="flex flex-col gap-1">
      <legend className="text-xs">{legend}</legend>
      {children}
    </fieldset>
  );
}

function ActionPicker({
  testId, mode, setMode, isAllowed,
}: {
  readonly testId: string;
  readonly mode: ActionMode;
  readonly setMode: Dispatch<SetStateAction<ActionMode>>;
  readonly isAllowed: (action: ReferentialAction) => boolean;
}) {
  return (
    <RadioGroup
      name={testId}
      value={mode}
      onValueChange={(v) => setMode(v as ActionMode)}
      className="flex flex-wrap items-center gap-3 text-xs"
    >
      <RadioOption testId={`${testId}-unspecified`} value="unspecified" label="Engine default" />
      {ALL_ACTIONS.map((action) => {
        const allowed = isAllowed(action);
        return (
          <RadioOption
            key={action}
            testId={`${testId}-${action}`}
            value={action}
            label={ACTION_LABELS[action]}
            disabled={!allowed}
            wrapperClass={allowed ? '' : 'opacity-50'}
            title={allowed ? undefined : 'Unsupported on this engine.'}
          />
        );
      })}
    </RadioGroup>
  );
}

function RadioRow<T extends string>({
  testId, mode, setMode, options,
}: {
  readonly testId: string;
  readonly mode: T;
  readonly setMode: Dispatch<SetStateAction<T>>;
  readonly options: readonly T[];
}) {
  return (
    <RadioGroup
      name={testId}
      value={mode}
      onValueChange={(v) => setMode(v as T)}
      className="flex flex-wrap items-center gap-3 text-xs"
    >
      {options.map((opt) => (
        <RadioOption
          key={opt}
          testId={`${testId}-${opt}`}
          value={opt}
          label={opt === 'unspecified' ? 'Engine default' : opt.replace(/^./, (c) => c.toUpperCase())}
        />
      ))}
    </RadioGroup>
  );
}

interface RadioOptionProps {
  readonly testId: string;
  readonly value: string;
  readonly label: string;
  readonly disabled?: boolean;
  readonly wrapperClass?: string;
  readonly title?: string;
}
function RadioOption({ testId, value, label, disabled, wrapperClass, title }: RadioOptionProps) {
  return (
    <label className={`flex items-center gap-1 ${wrapperClass ?? ''}`}>
      <RadioGroupItem value={value} disabled={disabled} title={title} data-testid={testId} />
      <span>{label}</span>
    </label>
  );
}
